/**
 * "System context" (docs section 2): one row per flow, read from left to right. The four
 * sources I2Q reads (shared mailboxes, IMDP, the OmniAnalytics warehouse, Dynamics CE),
 * the job that copies each one, and the database the job writes. The fifth row is the
 * app between the browser and the two databases. Under the rows: Azure OpenAI in the EU
 * and Key Vault, which the app and the jobs call. An arrow from a source carries "read
 * only". On a phone each row is a stack with the arrows down (the narrow media query).
 */

import {
  ArrowDown,
  ArrowRight,
  Database,
  Factory,
  Icon,
  type IconComponent,
  KeyRound,
  Lock,
  Mail,
  Monitor,
  Server,
  Sparkles,
  Warehouse,
} from "@norican/icons";
import { Badge, Card, cn, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const SYSTEM_CONTEXT_TITLE = "System context";

/** What the drawing shows, for a screen reader. */
export const SYSTEM_CONTEXT_SUMMARY =
  "Five flows: each source, the job or the app that reads it, and the database it writes.";

/** A box: an icon, a name and one short line (backticks mark code names). */
type Node = { icon: IconComponent; name: string; note: string };

/** One row. Every flow but `app` starts at a source that I2Q only reads. */
type Flow = { id: "mail" | "imdp" | "oa" | "crm" | "app"; from: Node; via: Node; to: Node };

const APP_DB: Node = { icon: Database, name: "App DB", note: "`rfq_foundry`" };
const mirror = (note: string): Node => ({ icon: Database, name: "Mirror", note });

/** The flows in the order of the external sources table of the fact sheet, then the app. */
export const SYSTEM_CONTEXT_FLOWS: readonly Flow[] = [
  {
    id: "mail",
    from: { icon: Mail, name: "Shared mailboxes", note: "Graph `Mail.Read`, GET only." },
    via: { icon: Server, name: "mail-ingest job", note: "Every 5 minutes." },
    to: APP_DB,
  },
  {
    id: "imdp",
    from: { icon: Factory, name: "IMDP", note: "The F&O replica on Azure SQL." },
    via: { icon: Server, name: "imdp-sync job", note: "Hourly, at minute 17." },
    to: mirror("`rfq_phase0`: `mirror.*`, `brain.*`"),
  },
  {
    id: "oa",
    from: { icon: Warehouse, name: "OmniAnalytics warehouse", note: "The F&O quotations." },
    via: { icon: Server, name: "oa-sync job", note: "Hourly, at minute 47." },
    to: mirror("`mirror.oa_quote_line`, `mirror.oa_quote_hdr`"),
  },
  {
    id: "crm",
    from: { icon: Warehouse, name: "Dynamics CE", note: "The installed base of the customers." },
    via: { icon: Server, name: "crm-sync job", note: "Nightly, at 03:23 UTC." },
    to: mirror("`mirror.ce_*`, `brain.crm_*`"),
  },
  {
    id: "app",
    from: { icon: Monitor, name: "Browser", note: "It calls `/api` on the same host." },
    via: { icon: Server, name: "The app", note: "The frontend and the API." },
    to: { icon: Database, name: "Both databases", note: "The app never writes the mirror." },
  },
];

/** The two services the app and the jobs call. */
export const SYSTEM_CONTEXT_SERVICES: readonly Node[] = [
  {
    icon: Sparkles,
    name: "Azure OpenAI in the EU",
    note: "The API, the mail-ingest job and the imdp-sync job call it. A host allow-list keeps every call in the EU.",
  },
  {
    icon: KeyRound,
    name: "Key Vault",
    note: "It holds the secrets. The app and the jobs read them with a managed identity.",
  },
];

export const SYSTEM_CONTEXT_RULE =
  "I2Q writes only to its two databases. It sends no mail and it writes nothing to F&O.";

/** The word on every arrow that leaves a source. */
const READ_ONLY = "read only";
/** The column heads of the grid form, on the first, third and fifth track. */
const COLUMNS = ["Source", "Job or app of I2Q", "Database of I2Q"];
const COLUMN_AT = ["col-start-1", "col-start-3", "col-start-5"];
const ROW = "grid grid-cols-[1fr_5.5rem_1fr_5.5rem_1fr] items-center gap-1";
const STACK = "border-border-subtle flex flex-col gap-1 border-b pb-2";
const ARROW = "text-fg-muted flex items-center justify-center gap-1";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

function NodeBox({ node, own = false }: { node: Node; own?: boolean }) {
  return (
    <Card
      variant="outline"
      padding="sm"
      accent={own ? "left" : undefined}
      className="flex min-w-0 flex-col gap-0.5"
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon icon={node.icon} size="sm" className="text-fg-muted shrink-0" />
        <Typography variant="label" className="min-w-0 break-words">
          {node.name}
        </Typography>
      </span>
      <Typography variant="caption" tone="muted" className="break-words">
        {code(node.note)}
      </Typography>
    </Card>
  );
}

/** An arrow with its word: right in the grid, down in the stack; a lock for "read only". */
function Arrow({ label, down }: { label: string; down: boolean }) {
  const locked = label === READ_ONLY;
  return (
    <span className={cn(ARROW, !down && "flex-col")}>
      <Badge tone={locked ? "neutral" : "accent"} variant={locked ? "outline" : "subtle"} size="sm">
        {locked && <Icon icon={Lock} size="xs" />}
        {label}
      </Badge>
      <Icon icon={down ? ArrowDown : ArrowRight} size="sm" label="to" />
    </span>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function SystemContextFigure(props: { caption?: string; layout?: "grid" | "stacked" }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "grid");
  const stacked = form === "stacked";
  return (
    <Figure name="system-context" title={SYSTEM_CONTEXT_TITLE} caption={props.caption}>
      {!stacked && (
        <div className={ROW}>
          {COLUMNS.map((column, index) => (
            <Typography key={column} variant="caption" tone="muted" className={COLUMN_AT[index]}>
              {column}
            </Typography>
          ))}
        </div>
      )}
      <ul aria-label={SYSTEM_CONTEXT_SUMMARY} data-layout={form} className="flex flex-col gap-2">
        {SYSTEM_CONTEXT_FLOWS.map((flow) => (
          <li key={flow.id} className={stacked ? STACK : ROW}>
            <NodeBox node={flow.from} />
            <Arrow label={flow.id === "app" ? "calls" : READ_ONLY} down={stacked} />
            <NodeBox node={flow.via} own />
            <Arrow label={flow.id === "app" ? "reads, writes" : "writes"} down={stacked} />
            <NodeBox node={flow.to} own />
          </li>
        ))}
      </ul>
      <div className={stacked ? "flex flex-col gap-2" : "grid grid-cols-2 gap-2"}>
        {SYSTEM_CONTEXT_SERVICES.map((service) => (
          <NodeBox key={service.name} node={service} />
        ))}
      </div>
      <Typography variant="body-sm" className="flex items-start gap-1.5">
        <Icon icon={Lock} size="sm" className="text-fg-muted mt-0.5 shrink-0" />
        <span className="min-w-0 break-words">{SYSTEM_CONTEXT_RULE}</span>
      </Typography>
    </Figure>
  );
}
